import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild, ChangeDetectionStrategy, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Grid as InavistaGrid, GridConfig, GridHyperlinkClickEvent } from '@inavista/components/grid';
import { OrderTypes } from 'projects/service/src/app/shared/models/app.model';
import { ShipmentAdjustmentOrder } from 'projects/service/src/app/shared/models/shipment-adjustment-order.model';
import { CommonService } from 'projects/service/src/app/shared/services/common.service';
import { exportInavistaGrid } from 'projects/service/src/app/shared/utils/inavista-grid-export';
import { LocalRowsGridApi, provideLocalGridRows } from 'projects/service/src/app/shared/utils/local-rows-grid-api';
import { statusPillColor } from 'projects/service/src/app/shared/utils/status-pill-colors';
import { MasterItemcodePopupComponent } from 'projects/shared/src/lib/components/master-itemcode-popup/master-itemcode-popup.component';
import { buildShipmentAdjustmentExceptionsGridConfig, SHIPMENT_ADJUSTMENT_EXCEPTIONS_COLUMNS } from './shipment-adjustment-exceptions.grid.config';

// The old grid had no height and showed every row. No paging now (Martin): the grid is as tall as its rows, up to 20,
// then scrolls. Toolbar + header + horizontal scrollbar, and the row height of the config.
const GRID_CHROME_PX = 92;
const ROW_PX = 30;
const MAX_VISIBLE_ROWS = 20;
const MIN_VISIBLE_ROWS = 2;

@Component({
    selector: 'app-shipment-adjustment-order-popup-exceptions-tab',
    templateUrl: './shipment-adjustment-order-popup-exceptions-tab.component.html',
    styleUrls: ['./shipment-adjustment-order-popup-exceptions-tab.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
    // The grid reads data.electronicAdjustmentOrderLines, not an endpoint.
    providers: [...provideLocalGridRows()]
})
export class ShipmentAdjustmentOrderPopupExceptionsTabComponent implements OnInit, OnChanges {

  @Input() data: ShipmentAdjustmentOrder;

  @ViewChild('shipmentAdjustmentExceptionGrid') shipmentAdjustmentExceptionGrid: InavistaGrid;
  showGrid: boolean = false;
  gridConfig: GridConfig = null;
  gridHeight = GRID_CHROME_PX + MIN_VISIBLE_ROWS * ROW_PX;
  linkedPopupData: { id: any; type: OrderTypes; };
  private readonly gridRows = inject(LocalRowsGridApi);

  constructor(
    private commonService: CommonService,
    private dialog: MatDialog,
  ) { }

  showMasterItemCodePopup(eqData: any) {
    if (!eqData?.itemCode) return;
    const dialogRef = this.dialog.open(MasterItemcodePopupComponent, {
      panelClass: 'master-itemcode-popup-dialog',
      width: '80%',
      height: '80%',
      data: eqData
    });
    dialogRef.afterClosed().subscribe((result) => { });
  }

  ngOnInit(): void {
    this.gridRows.columns.set(LocalRowsGridApi.schema(SHIPMENT_ADJUSTMENT_EXCEPTIONS_COLUMNS));
    this.gridConfig = buildShipmentAdjustmentExceptionsGridConfig(
      !!this.commonService.roleClaims['AllGrid_Toolbar_Excel_Export']?.visible,
      statusPillColor(s => this.commonService.getStatusClass(s)));
    this.refreshRows();
    setTimeout(() => {
      this.showGrid = true;
    }, 500);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && !changes['data'].firstChange) {
      this.refreshRows();
    }
  }

  private lines(): any[] {
    const lines = this.data?.electronicAdjustmentOrderLines;
    return Array.isArray(lines) ? lines : [];
  }

  // The rows the grid shows: the order's lines as they came, numbered.
  refreshRows() {
    const lines = this.lines();
    this.gridRows.rows.set(lines.map((line: any, i) => ({ ...line, __rowKey: i })));
    this.gridHeight = GRID_CHROME_PX + Math.min(Math.max(lines.length, MIN_VISIBLE_ROWS), MAX_VISIBLE_ROWS) * ROW_PX;
    this.shipmentAdjustmentExceptionGrid?.refresh();
  }

  // Shipment Order Id opens the shipment order; Item and Item Description the item, as before.
  onHyperlinkClick(event: GridHyperlinkClickEvent) {
    const line = this.lines()[event.row['__rowKey'] as number] ?? event.row;
    if (event.columnId === 'shipmentOrderId') {
      this.openShipmentOrderPopup(line.shipmentOrderId);
    } else {
      this.showMasterItemCodePopup(line);
    }
  }

  // Fixed (Martin, 2026-10-28): the old Excel button showed (AllGrid_Toolbar_Excel_Export) but had no click handler, so
  // it did nothing; it now exports the rows shown, as every other grid's Excel button.
  onExportRequested() {
    exportInavistaGrid(this.shipmentAdjustmentExceptionGrid, 'Shipment Adjustment Order Exceptions');
  }

  openShipmentOrderPopup(shipmentOrderId){
    this.linkedPopupData = {
        id: shipmentOrderId,
        type: OrderTypes.shipmentOrder
    };
  }
}
